import { Controller } from '@hotwired/stimulus'
import { Chart, BarController, BarElement, CategoryScale, LinearScale, Tooltip } from 'chart.js'
import { formatCurrency, formatCompactCurrency } from '../utils/currency'

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip)

export default class extends Controller {
  static targets = ['canvas', 'cumulativeAmount', 'interestsAmount', 'sponsorshipAmount', 'bonusAmount', 'filterButton']
  static values = {
    data: Object,
    activePeriod: String,
    colors: { type: Array, default: [] },
  }

  connect() {
    this.#initChart()
    this.#syncFilterButtons()
    this.#syncDisplay()
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
  }

  selectPeriod(event) {
    this.activePeriodValue = event.params.period
  }

  activePeriodValueChanged() {
    if (!this.chart) return
    this.#updateChart()
    this.#syncFilterButtons()
    this.#syncDisplay()
  }

  // Private

  #initChart() {
    const period = this.#currentPeriod()
    this.chart = new Chart(this.canvasTarget, {
      type: 'bar',
      data: this.#buildChartData(period),
      options: {
        responsive: true,
        categoryPercentage: 0.4,
        barPercentage: 0.9,
        scales: {
          x: {
            stacked: true,
            grid: { display: false },
            ticks: {
              callback: (_value, index) => {
                const months = this.#currentPeriod().months
                const month = months[index]
                if (!month) return ''
                return this.#isYearlyPeriod() ? month.label : [month.label, month.year]
              },
            },
          },
          y: {
            stacked: true,
            beginAtZero: true,
            grid: {
              borderDash: [4, 4],
            },
            ticks: {
              callback: (value) => formatCompactCurrency(value),
            },
          },
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            displayColors: false,
            callbacks: {
              title: (tooltipItems) => {
                const months = this.#currentPeriod().months
                const month = months[tooltipItems[0].dataIndex]
                return month ? (this.#isYearlyPeriod() ? month.label : `${month.label} ${month.year}`) : ''
              },
              label: (context) => formatCurrency(context.parsed.y),
            },
          },
        },
        onHover: (_event, elements) => {
          const month = elements.length > 0
            ? this.#currentPeriod().months[elements[0].index]
            : null
          this.#syncDisplay(month)
        },
      },
    })
  }

  #updateChart() {
    const period = this.#currentPeriod()
    const chartData = this.#buildChartData(period)
    this.chart.data.labels = chartData.labels
    this.chart.data.datasets.forEach((dataset, i) => {
      dataset.data = chartData.datasets[i].data
      dataset.borderRadius = chartData.datasets[i].borderRadius
    })
    this.chart.update()
  }

  #buildChartData(period) {
    const months = period.months
    const [interestsColor, sponsorshipColor, bonusColor] = this.colorsValue
    return {
      labels: months.map(m => m.label),
      datasets: [
        {
          label: '',
          data: months.map(m => m.sponsorship),
          backgroundColor: sponsorshipColor,
          borderRadius: months.map(m => (m.bonus > 0 || m.interests > 0) ? 0 : 4),
        },
        {
          label: '',
          data: months.map(m => m.bonus),
          backgroundColor: bonusColor,
          borderRadius: months.map(m => m.interests > 0 ? 0 : 4),
        },
        {
          label: '',
          data: months.map(m => m.interests),
          backgroundColor: interestsColor,
          borderRadius: 4,
        },
      ],
    }
  }

  #syncDisplay(month = null) {
    const period = this.#currentPeriod()

    this.cumulativeAmountTarget.textContent = formatCurrency(period.cumulative)
    this.interestsAmountTarget.textContent = formatCurrency(
      month ? month.interests : period.cumulative_interests
    )
    this.bonusAmountTarget.textContent = formatCurrency(
      month ? month.bonus : period.cumulative_bonus
    )
    this.sponsorshipAmountTarget.textContent = formatCurrency(
      month ? month.sponsorship : period.cumulative_sponsorship
    )
  }

  #syncFilterButtons() {
    this.filterButtonTargets.forEach(button => {
      const isActive = button.dataset.profitsBarChartPeriodParam === this.activePeriodValue
      button.classList.toggle('active', isActive)
    })
  }

  #currentPeriod() {
    return this.dataValue[this.activePeriodValue]
  }

  #isYearlyPeriod() {
    return this.#currentPeriod().grouped_by === 'year'
  }

}
